import { OrganizationsListModel } from '@/modules/organizations'
import {
    GetBillingTemplate,
    Address,
    ContactData,
    EmailDeliveryPolicy,
    DeliveryPolicyType
} from '@/modules/billing-templates'

export enum AgreementType {
    Total = 'total',
    Licence = 'licence'
}

export type AgreementPackage = {
    packageId: string,
    code: string,
    name: string
}

export type AgreementContactPerson = {
    name: string,
    email: string,
    types: string[],
    emailIdentifier: string
}

export type Agreement = {
    billingInfoId: string,
    cancelPeriod: number,
    draft: true,
    endDate: string | null,
    formEnabled: true,
    formId: string,
    formUrl: string | null,
    agreementId: string,
    shortId: number,
    invoiceAllowedFrom: string,
    invoicingPeriod: number,
    memberLimit?: number,
    minimalLength: number,
    name: string,
    organizationId: number,
    packages: AgreementPackage[],
    price: number,
    startDate: string,
    status: string,
    type: AgreementType,
    contacts: AgreementContactPerson[],
    allowMarketingCommunication: boolean,
    excludeDigitalClassrooms: boolean,
    isReadyForPublication: boolean,
    canceledAt: string,
    createdAt: string,
    additionalInfo: string,
    allowLSPExport: boolean,
    editableInSelfService: boolean
}

export type AgreementsListModel = Agreement

export type GetAgreementsList = {
    count: number,
    results: AgreementsListModel[]
}

export type AgreementsSimplifiedListModel = {
    agreementId: string,
    name: string,
    organizationId: string,
    shortId: number
}

export type AgreementMember = {
    agreementId: string,
    id: string,
    name: string,
    email: string,
    startDate: string,
    endDate: string,
    isActive: boolean,
    cancelled: string,
    removedPermanently: string,
    expired: boolean,
    possibleCancellationDate: string,
    accessId: string
}

export type SelectAgreementPackage = {
    value: string,
    label: string
}

export type SelectOrganization = Pick<OrganizationsListModel, 'id' & 'companyName'>

export type AgreementPayload = {
    type: string,
    name: string,
    startDate: string,
    endDate: string | null,
    invoicingAllowedFrom: string,
    price: number | string,
    maxMembers: number | string,
    minLength: number | string,
    cancelPeriod: number | null,
    invoicingPeriod: number | string,
    billingTemplateId: string,
    organizationId: string,
    packages: string[],
    allowMarketingCommunication: boolean,
    excludeDigitalClassrooms: boolean,
    additionalInfo: string | null,
    allowLSPExport: boolean,
    editableInSelfService: boolean
}

export type GetAgreementBillingTemplate = GetBillingTemplate

export type BasicBillingInfo = {
    agreementId: string
    organizationName: string,
    businessId: string,
    additionalInfo?: string
    paymentTerms?: number
    invoiceDescription?: string | null,
    vatId: string,
    customerNumber: string,
    reference: string
} & ContactData & Address

export type ElectronicDeliveryPolicy = {
    electronicAddress: string,
    electronicOperatorId?: string,
    electronicOperatorCode?: string,
}

export type AgreementBillingInfoMailPayload = BasicBillingInfo

export type AgreementBillingInfoEmailPayload = BasicBillingInfo & EmailDeliveryPolicy

export type AgreementBillingInfoElectronicPayload = BasicBillingInfo & ElectronicDeliveryPolicy

export type AgreementAccessIdentifier = {
    agreementId: string,
    accessId: string,
}

export type AgreementScheduledPrice = {
    id: string,
    agreementId: string,
    validFrom: string,
    price: number
}

export type AgreementScheduledPriceCreatePayload = {
    agreementId: string,
    validFrom: string,
    price: number
}

export type AgreementScheduledPriceUpdatePayload = AgreementScheduledPrice

export type AgreementScheduledPriceDeletePayload = {
    id: string,
    agreementId: string
}

export type AgreementContactInfoPayload = {
    name: string,
    email: string,
    types: string[]
}

export type AgreementContactInfoCreatePayload = AgreementContactInfoPayload
export type AgreementContactInfoUpdatePayload = AgreementContactInfoPayload

export type AgreementContactInfoDeletePayload = {
    email: string
}

export type SwapMember = AgreementAccessIdentifier & {
    newMemberId: string
}

export type CancelMember = AgreementAccessIdentifier & {
    endDate: string,
    memberId: string
}

export type PurgeMember = AgreementAccessIdentifier & {
    memberId: string
}

export type GetAgreementBillingInfo = Omit<BasicBillingInfo, 'agreementId'> & {
    billingInfoId: string,
    organizationId: string,
    deliveryPolicyType: DeliveryPolicyType,
    deliveryPolicy: ElectronicDeliveryPolicy & EmailDeliveryPolicy
}

export type AgreementBillingInfoIdentifier = {
    agreementId: string,
    billingInfoId: string
}

export type ChangeBillingInfoOrganizationName = AgreementBillingInfoIdentifier & {
    organizationName: string
}

export type ChangeBillingInfoBusinessId = AgreementBillingInfoIdentifier & {
    businessId: string
}

export type ChangeBillingInfoAdditionalInfo = AgreementBillingInfoIdentifier & {
    additionalInfo: string
}

export type ChangeBillingInfoPaymentTerms = AgreementBillingInfoIdentifier & {
    paymentTerms: number | null
}

export type ChangeBillingInfoInvoiceDescription = AgreementBillingInfoIdentifier & {
    invoiceDescription: string | null
}

export type ChangeBillingInfoInvoiceReference = { reference: string }

export type ChangeBillingInfoContactData = AgreementBillingInfoIdentifier & ContactData

export type ChangeBillingInfoAddress = AgreementBillingInfoIdentifier & Address

export type ChangeBillingInfoVatId = AgreementBillingInfoIdentifier & {
    vatId: string
}

export type ChangeBillingInfoCustomerNumber = AgreementBillingInfoIdentifier & {
    customerNumber: string
}

export type AssignElectronicDeliveryPolicy = AgreementBillingInfoIdentifier & ElectronicDeliveryPolicy

export type AssignEmailDeliveryPolicy = AgreementBillingInfoIdentifier & EmailDeliveryPolicy

export type AssignMailDeliveryPolicy = AgreementBillingInfoIdentifier

export type MemberInAgreement = {
    startDate: string,
    endDate: string | null,
    memberId: string,
    firstName: string,
    lastName: string,
    email: string,
    agreementId: string,
    agreementName: string,
    organizationId: string,
    organizationName: string,
    accessStatus: string
}

export type GetMembersInAgreementsList = {
    count: number,
    results: MemberInAgreement[]
}
